// client/src/components/Vehicles/VehicleForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../api/axiosConfig';

const createVehicle = async (data) => axios.post('/vehicles', data);
const updateVehicle = async ({ id, data }) => axios.put(`/vehicles/${id}`, data);
const fetchClients = async () => {
  const { data } = await axios.get('/clients');
  return data;
};

const VehicleForm = ({ open, onClose, vehicle, onSuccess }) => {
  const [form, setForm] = useState({
    licensePlate: '',
    vin: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    clientId: '',
    isActive: true,
  });
  const [error, setError] = useState('');
  const queryClient = useQueryClient();
  const { data: clients } = useQuery({ queryKey: ['clients'], queryFn: fetchClients });

  useEffect(() => {
    if (vehicle) {
      setForm({
        licensePlate: vehicle.licensePlate || '',
        vin: vehicle.vin || '',
        brand: vehicle.brand || '',
        model: vehicle.model || '',
        year: vehicle.year || new Date().getFullYear(),
        clientId: vehicle.clientId?._id || vehicle.clientId || '',
        isActive: vehicle.isActive !== undefined ? vehicle.isActive : true,
      });
    } else {
      setForm({
        licensePlate: '',
        vin: '',
        brand: '',
        model: '',
        year: new Date().getFullYear(),
        clientId: '',
        isActive: true,
      });
    }
  }, [vehicle, open]);

  const mutation = useMutation({
    mutationFn: vehicle ? updateVehicle : createVehicle,
    onSuccess: () => {
      queryClient.invalidateQueries(['vehicles']);
      onSuccess();
      onClose();
    },
    onError: (err) => setError(err.response?.data?.message || 'Error saving vehicle'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.licensePlate) {
      setError('License plate is required');
      return;
    }
    if (!form.clientId) {
      setError('Please select a client');
      return;
    }
    mutation.mutate(vehicle ? { id: vehicle._id, data: form } : form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{vehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="License Plate"
                required
                value={form.licensePlate}
                onChange={(e) => setForm({ ...form, licensePlate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="VIN"
                value={form.vin}
                onChange={(e) => setForm({ ...form, vin: e.target.value })}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Brand"
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Model"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={form.year}
                onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) || new Date().getFullYear() })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Client"
                required
                value={form.clientId}
                onChange={(e) => setForm({ ...form, clientId: e.target.value })}
              >
                <MenuItem value="">Select client</MenuItem>
                {clients?.map((client) => (
                  <MenuItem key={client._id} value={client._id}>
                    {client.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    color="primary"
                  />
                }
                label={form.isActive ? 'Active' : 'Inactive'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default VehicleForm;